import React, { FC, useCallback, useState, useMemo } from 'react';
import { Cell } from 'react-table';
import styled from 'styled-components';
import { createPortal } from 'react-dom';

import { CashieringTable } from './components/CashieringTable';
import { CashieringFilters } from './components/CashieringFilters';
import { Button, ColumnWithSorting } from '@tradingblock/components';
import { useLatestPendingCashieringDate } from '../../../hooks/useLatestPendingCashieringDate';
import { useDispatch } from 'react-redux';
import { AdminCashieringActions } from '../../../data/global/actions/admin/AdminCashieringActions';
import { CashieringBaseEntity, ClientEntity } from '@tradingblock/types';
import { TransactionCell } from './components/table/CashieringTransactionCell';
import { CashieringMethodCell } from './components/table/CashieringMethodCell';
import dayjs from 'dayjs';
import { CashieringNoteCell } from './components/table/CashieringNoteCell';
import { getIdNumber } from '@tradingblock/api';
import { useCashieringActions } from './useCashiering';
import { useStateSelector } from '../../../data/global/dataSelectors';
import { AccountActions } from '../../../data/global/actions/AccountActions';
import { LinkedAccountsActions } from '../../../data/global/actions/LinkedAccountsActions';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import { AdminCashieringDetail } from './AdminCashieringDetail';

// Add statuses and getAccountStatus logic from AccountTable
const statuses = [
  { id: 1, label: 'Prospect', data: 'Prospect' },
  { id: 2, label: 'Prospect Application Started', data: 'ProspectApplicationStarted' },
  { id: 3, label: 'Prospect Application Completed', data: 'ProspectApplicationCompleted' },
  { id: 4, label: 'Prospect Application Expired', data: 'ProspectApplicationExpired' },
  { id: 5, label: 'Processing', data: 'Processing' },
  { id: 6, label: 'Processing Items Missing', data: 'ProcessingItemsMissing' },
  { id: 7, label: 'Active Not Funded', data: 'ActiveNotFunded' },
  { id: 8, label: 'Active', data: 'Active' },
  { id: 14, label: 'Active Closing Trades Only', data: 'ActiveClosingTradesOnly' },
  { id: 9, label: 'Closed', data: 'Closed' },
  { id: 10, label: 'Closed Never Funded', data: 'ClosedNeverFunded' },
  { id: 11, label: 'Rejected', data: 'Rejected' },
  { id: 12, label: 'Deceased', data: 'Deceased' },
];

const getAccountStatus = (status: number | string) => {
  const foundStatus = statuses.find(s => s.id === status || s.data === status || s.label === status);
  if (foundStatus) {
    let color = '';
    let backgroundColor = '';
    switch (foundStatus.data) {
      case 'Active':
        color = 'var(--green-bright, #50d18d)';
        backgroundColor = 'var(--green-dark, rgba(40, 167, 69, 0.2))';
        break;
      case 'Inactive':
      case 'Closed':
        color = 'var(--salmon, #ff6b6b)';
        backgroundColor = 'var(--salmon-dark, rgba(255, 107, 107, 0.2))';
        break;
      case 'ActiveClosingTradesOnly':
        color = 'var(--gold, #ffbf47)';
        backgroundColor = 'var(--gold-dark, rgba(255, 191, 71, 0.2))';
        break;
      default:
        color = '#ffcc00';
        backgroundColor = 'rgba(255, 204, 0, 0.2)';
        break;
    }
    return (
      <span className="status-label" style={{ color, backgroundColor }}>
        {foundStatus.label}
      </span>
    );
  }
  return null;
};

// Styled Components
const DetailContainer = styled.div`
  background: var(--blue-dark);
  color: #fff;
  margin: 0;
  padding: var(--gap);
  overflow: hidden;
  border-radius: var(--radius);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--lighten);
`;

const DetailHeader = styled.div`
  background: var(--blue-dark);
  color: #fff;
  padding: 0 0 var(--gap) 0;
  margin-bottom: var(--gap);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--lighten);
`;

const HeaderTitle = styled.div`
  h2 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 300;
    color: #fff;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }

  p {
    margin: 4px 0 0 0;
    font-size: 0.75rem;
    opacity: 0.85;
    color: rgba(255, 255, 255, 0.85);
  }
`;

const BackButton = styled(Button)`
  background: none;
  border: 1px solid var(--lighten);
  color: var(--blue);
  padding: 0.5rem 1rem;
  border-radius: var(--radius);
  font-weight: normal;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background: var(--lighten);
    border-color: var(--lighten2x);
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  i {
    margin-right: 0.5rem;
  }
`;

const DetailContent = styled.div`
  padding: 0;
  background: var(--blue-dark);
  border-radius: 0 0 var(--radius) var(--radius);
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: var(--gap);
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  padding: 0.5rem 0;

  span {
    margin: 0 0.5rem;
    color: rgba(255, 255, 255, 0.3);
  }

  .current {
    color: var(--blue);
    font-weight: normal;
  }
`;

const TableContainer = styled.div`
  background: var(--blue-dark);
  color: #fff;
  margin: 0;
  overflow: hidden;
  border-radius: var(--radius);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--lighten);
  padding: var(--gap);
`;

const TableHeader = styled.div`
  background: var(--blue-dark);
  color: #fff;
  padding-bottom: var(--gap);
  border-bottom: 1px solid var(--lighten);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const TableTitle = styled.div`
  h2 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 300;
    color: #fff;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }

  p {
    margin: 4px 0 0 0;
    font-size: 0.75rem;
    opacity: 0.85;
    color: rgba(255, 255, 255, 0.85);
  }
`;

const TableContent = styled.div`
  padding: var(--gap);
  background: var(--blue-dark);
  border-radius: 0 0 var(--radius) var(--radius);
  overflow: hidden;
`;

const TableLayout = styled.div`
  /* Shared pane height: avoids magic 200px/400px while respecting dynamic toolbars (dvh) */
  --cashiering-chrome: 11rem;
  --cashiering-pane-height: min(92dvh, max(28rem, calc(100vh - var(--cashiering-chrome))));
  --cashiering-pane-height-stacked: min(78dvh, max(22rem, calc(100vh - 20rem)));

  display: flex;
  gap: var(--gap);
  position: relative;
  align-items: flex-start;

  .table-section {
    flex: 1 1 auto;
    min-width: 0;
    background: var(--blue-dark);
    border-radius: var(--radius);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border: 1px solid var(--lighten);
    overflow: auto;
    height: var(--cashiering-pane-height);
    min-height: min(28rem, 70vh);
    max-height: var(--cashiering-pane-height);
    position: relative;
  }

  .filters-section {
    flex: 0 0 clamp(280px, 28vw, 380px);
    max-width: 100%;
    background: var(--blue-dark);
    border-radius: var(--radius);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border: 1px solid var(--lighten);
    padding: 0;
    position: sticky;
    top: var(--gap);
    height: var(--cashiering-pane-height);
    min-height: min(28rem, 70vh);
    max-height: var(--cashiering-pane-height);
    overflow: hidden;
    z-index: 10;
    transition: box-shadow 0.3s ease-in-out, border-color 0.3s ease-in-out;
    display: flex;
    flex-direction: column;
    border-left: 3px solid var(--teal);

    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
      border-color: var(--lighten2x);
      border-left-color: var(--teal);
    }

    & > * {
      flex: 1 1 auto;
      min-height: 0;
    }

    .cashiering-filters {
      position: relative;
      top: 0;
    }
  }

  @media (max-width: 1200px) {
    flex-direction: column;
    align-items: stretch;

    .filters-section {
      flex: 1 1 auto;
      width: 100%;
      position: relative;
      top: 0;
      height: auto;
      max-height: none;
      min-height: 0;
      order: -1;
    }

    .table-section {
      height: var(--cashiering-pane-height-stacked);
      min-height: min(400px, 50vh);
      max-height: var(--cashiering-pane-height-stacked);
    }
  }
`;

const CashieringAdminReviewColumns: ColumnWithSorting<CashieringBaseEntity>[] = [
  {
    Header: 'Account',
    accessor: v => v.accountNumber,
    sortType: 'alphanumeric',
    id: 'AccountNumber',
  },
  {
    Header: 'Name',
    accessor: v => v.accountName,
    sortType: 'alphanumeric',
    id: 'AccountName',
  },
  {
    Header: 'Rep Code',
    accessor: v => v.repCode,
    sortType: 'alphanumeric',
    id: 'RepCode',
  },
  {
    Header: 'Transaction',
    id: 'transaction',
    Cell: ({ row }: Cell<CashieringBaseEntity>) => {
      const { transfer, status, relationship, transferRecipientBank, transferWireInstruction } = row.original;
      return (
        <TransactionCell {...{ transfer, status, relationship, transferRecipientBank, transferWireInstruction }} />
      );
    },
  },
  {
    Header: 'Method',
    id: 'method',
    Cell: ({ row }: Cell<CashieringBaseEntity>) => {
      const { transfer, relationship } = row.original;
      return <CashieringMethodCell {...{ transfer, relationship }} />;
    },
  },
  {
    Header: 'Date',
    accessor: v => v.date,
    sortType: 'alphanumeric',
    sortDescFirst: true,
    id: 'Date',
    Cell: ({ row }: Cell<CashieringBaseEntity>) => {
      const dateVal = dayjs(row.original.date);
      if (dateVal && dateVal.isValid()) {
        return (
          <>
            {dateVal.format('M/DD/YY')}
            <br />
            {dateVal.format('hh:mm A')}
          </>
        );
      }
      return <></>;
    },
  },
  {
    Header: 'Account Status',
    id: 'accountStatus',
    sortType: 'alphanumeric',
    Cell: ({ row }: Cell<CashieringBaseEntity>) => {
      const { accountStatus } = row.original;
      return getAccountStatus(accountStatus);
    },
  },
  {
    Header: 'Notes',
    id: 'note',
    Cell: ({ row }: Cell<CashieringBaseEntity>) => {
      const { note, adminNote, transfer, relationship, transferWireInstruction, accountId } = row.original;
      const id = transfer
        ? transfer.transferId
        : relationship
        ? relationship.relationshipId
        : transferWireInstruction
        ? transferWireInstruction.transferWireInstructionId
        : undefined;
      const type = transfer
        ? 'transfers'
        : relationship
        ? 'relationships'
        : transferWireInstruction
        ? 'wireInstructions'
        : undefined;
      if (id && type && (note || adminNote)) {
        return (
          <>
            <CashieringNoteCell accountId={accountId} id={getIdNumber(id)} type={type} />
          </>
        );
      }
      return <></>;
    },
  },
  {
    Header: 'Action',
    id: 'action',
    className: 'text-center',
    Cell: ({ row }: Cell<CashieringBaseEntity>) => {
      const { status } = row.original;
      const isPending = _.toLower(status).includes('pending');
      return (
        <div className="text-center">
          <Button className={`btn ${isPending ? `btn-outline-light` : ''}`}>{isPending ? 'Review' : 'View'}</Button>
        </div>
      );
    },
  },
  {
    Header: 'Select',
    id: 'switch',
    disableSortBy: true,
    Cell: ({ row }: Cell<ClientEntity>) => {
      const dispatch = useDispatch();
      const history = useHistory();
      const searchValue = row.original.accountNumber
        ? row.original.accountNumber.trim()
        : row.original.accountName.trim();
      const selectedLinkedAccountId = useStateSelector(
        state => state.account.selectedLinkedAccount && state.account.selectedLinkedAccount.accountId
      );

      const isSelected = selectedLinkedAccountId === row.original.accountId;
      const accountSelectedCallback = (accountId: number) => {
        dispatch(
          AccountActions.retrieveSetAccountData({
            accountId,
            searchOptions: {
              page: 0,
              pageSize: 5,
              sortBy: '',
              sortByOrder: 'desc',
              search: searchValue,
              status: '',
            },
          })
        );
        dispatch(AccountActions.requestSubAccounts({ accountId: accountId }));
        history.push('/');
      };

      return (
        <div className="text-center">
          <button
            disabled={isSelected}
            onClick={() => {
              dispatch(
                LinkedAccountsActions.requestLinkedAccounts({
                  page: 0,
                  pageSize: 5,
                  sortBy: '',
                  sortByOrder: 'desc',
                  search: searchValue,
                  status: '',
                })
              );
              setTimeout(() => accountSelectedCallback(row.original.accountId), 1000);
            }}
          >
            {isSelected ? <i className="fas fa-circle" /> : <i className="fal fa-check-circle" />}
          </button>
        </div>
      );
    },
    width: '100px',
  },
  {
    Header: 'Filter',
    id: 'filter',
    Cell: ({ row }: Cell<CashieringBaseEntity>) => {
      // This will be handled in columnsWithViewDetail
      return (
        <div className="text-center">
          <button>
            <i className="fal fa-filter" />
          </button>
        </div>
      );
    },
  },
];

const CASHIERING_TABLE_DATE_COLUMN = 'Date';

const CashieringHeader: FC<{}> = () => {
  const dispatch = useDispatch();

  const [newPendingRequestsExist, setNewPendingRequestsExist] = useState(false);
  const handleNewCashieringRequests = useCallback(() => setNewPendingRequestsExist(true), []);
  useLatestPendingCashieringDate(handleNewCashieringRequests, true);

  const refreshCashieringData = useCallback(() => {
    setNewPendingRequestsExist(false);
    dispatch(
      AdminCashieringActions.refresh({ sortBy: CASHIERING_TABLE_DATE_COLUMN, sortByOrder: 'desc', replace: true })
    );
  }, []);

  return (
    <>
      {newPendingRequestsExist && (
        <span style={{ display: 'inline-block' }}>
          {'New requests have been received. '}
          <a href="#" onClick={refreshCashieringData}>
            Refresh list
          </a>
          {' to see them. '}
        </span>
      )}
    </>
  );
};

// Toast styles (reuse from detail view)
const ToastContainer = styled.div`
  position: fixed;
  top: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  min-width: 320px;
  max-width: 90vw;
`;
const Toast = styled.div<{ success?: boolean }>`
  background: ${({ success }) => (success ? 'var(--teal-light)' : 'var(--salmon)')};
  color: ${({ success }) => (success ? 'var(--blue-dark)' : '#fff')};
  padding: 1rem 2rem;
  border-radius: var(--radius);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 1em;
  margin-bottom: 1rem;
`;
const ToastClose = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  margin-left: 1em;
  cursor: pointer;
`;

export const AdminCashieringIndex = () => {
  const [selectedRowIndex, setSelectedRowIndex] = React.useState<number | undefined>(undefined);
  const [currentDetailItem, setCurrentDetailItem] = React.useState<{
    id: string;
    entityType: string;
    accountId: number;
  } | null>(null);
  const [searchValue, setSearchValue] = React.useState<string>('');
  const [toast, setToast] = React.useState<{ success: boolean; message: string } | null>(null);
  const tableId = useMemo(
    () =>
      `cashiering-table-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
    []
  );

  // Get the cashiering actions to reset filters
  const {
    search: onSearch,
    repCode: onRepCodeSearch,
    setTimeframe,
    setType,
    setStatus,
    setAccountStatus,
  } = useCashieringActions();

  const handleSetSelectedRowIndex = useCallback((index: number | undefined) => {
    setSelectedRowIndex(index);
  }, []);

  const handleViewDetail = useCallback((id: string, entityType: string, accountId: number) => {
    setCurrentDetailItem({ id, entityType, accountId });
  }, []);

  const handleBackToList = useCallback(() => {
    // Reset all filters when navigating back to list, similar to Clear All button
    onSearch('');
    onRepCodeSearch(undefined);
    setTimeframe(undefined, undefined);
    setType(undefined);
    setStatus(undefined);
    setAccountStatus(undefined);
    sessionStorage.removeItem('cashieringSearch');
    // Don't remove the table-specific sessionStorage item to preserve row highlighting
    setSearchValue('');
    setCurrentDetailItem(null);
  }, [onSearch, onRepCodeSearch, setTimeframe, setType, setStatus, setAccountStatus]);

  // New: handleViewAllEntries
  const handleViewAllEntries = useCallback((accountNumber: string) => {
    setCurrentDetailItem(null);
    setSearchValue(accountNumber);
  }, []);

  // New: handleSaveComplete
  const handleSaveComplete = useCallback((toastMsg?: { success: boolean; message: string }) => {
    setCurrentDetailItem(null);
    if (toastMsg && toastMsg.success) {
      setTimeout(() => setToast(toastMsg), 100); // show after navigation
    }
  }, []);

  // New: handleFilterButtonClick - callback to update searchValue when filter button is clicked
  const handleFilterButtonClick = useCallback((filterValue: string) => {
    setSearchValue(filterValue);
  }, []);

  // ESC key handler to clear highlight
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedRowIndex !== undefined) {
        setSelectedRowIndex(undefined);
        // Clear sessionStorage as well
        sessionStorage.removeItem(`cashieringSearch-${tableId}`);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedRowIndex, tableId]);

  // Ref for scrolling to the detail header
  const detailHeaderRef = React.useRef<HTMLDivElement>(null);
  const prevDetailItemRef = React.useRef<typeof currentDetailItem>(null);

  // Track when returning from detail page to trigger scroll in table
  const [shouldScrollToSelected, setShouldScrollToSelected] = React.useState(false);

  React.useEffect(() => {
    // Check if we're returning from detail BEFORE updating the ref
    const wasOnDetail = prevDetailItemRef.current !== null;
    const isNowOnList = currentDetailItem === null;

    if (wasOnDetail && isNowOnList) {
      setShouldScrollToSelected(true);
    }

    // Update ref after checking
    prevDetailItemRef.current = currentDetailItem;

    // Scroll to detail header when it's displayed (only if not already visible)
    if (currentDetailItem && detailHeaderRef.current) {
      // Add a small delay to make the transition less jarring
      setTimeout(() => {
        if (detailHeaderRef.current) {
          const rect = detailHeaderRef.current.getBoundingClientRect();
          const isVisible = rect.top >= 0 && rect.top < window.innerHeight;

          // Only scroll if the header is not already visible in the viewport
          if (!isVisible) {
            detailHeaderRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest',
            });
          }
        }
      }, 300);
    }
  }, [currentDetailItem]);

  // Create columns with the onViewDetail function and filter button callback
  const columnsWithViewDetail = React.useMemo(() => {
    return CashieringAdminReviewColumns.map(column => {
      if (column.id === 'action') {
        return {
          ...column,
          Cell: ({ row }: Cell<CashieringBaseEntity>) => {
            const { status, transfer, relationship, transferWireInstruction, accountId, type } = row.original;

            // Determine the entity type and ID for navigation
            let entityType: string;
            let entityId: string;

            if (transfer) {
              entityType = 'transfers';
              entityId = transfer.transferId;
            } else if (relationship) {
              entityType = 'relationships';
              entityId = relationship.relationshipId;
            } else if (transferWireInstruction) {
              entityType = 'wireInstructions';
              entityId = transferWireInstruction.transferWireInstructionId.toString();
            } else {
              console.warn('No valid entity found for row:', row.original);
              return <></>;
            }

            const isPending = _.toLower(status).includes('pending');

            return (
              <div className="text-center">
                <Button
                  className={`btn ${isPending ? `btn-outline-light` : ''}`}
                  onClick={() => {
                    // Store the current detail item in sessionStorage for potential back navigation
                    sessionStorage.setItem(`cashieringSearch-${tableId}`, JSON.stringify({ index: row.index }));
                    // Call the onViewDetail function
                    handleViewDetail(entityId, entityType, accountId);
                  }}
                >
                  {isPending ? 'Review' : 'View'}
                </Button>
              </div>
            );
          },
        };
      } else if (column.id === 'filter') {
        return {
          ...column,
          Cell: ({ row }: Cell<CashieringBaseEntity>) => {
            const { search: onSearch, setPage, setType, setStatus } = useCashieringActions();
            // searchValue should default to accountNumber, if no accountNumber, then use accountName
            const searchValue = row.original.accountNumber
              ? row.original.accountNumber.trim()
              : row.original.accountName.trim();
            const handleSetFilter = useCallback(() => {
              setPage(0);
              setType([]);
              setStatus([]);
              onSearch(searchValue);
              // Update the local searchValue state to populate the search input
              handleFilterButtonClick(searchValue);
              sessionStorage.removeItem('cashieringSearch');
              row.original.handleSetSelectedRow(undefined);
            }, [row.original.accountId]);

            return (
              <div className="text-center">
                <button onClick={() => handleSetFilter()}>
                  <i className="fal fa-filter" />
                </button>
              </div>
            );
          },
        };
      }
      return column;
    });
  }, [handleViewDetail, handleFilterButtonClick, tableId]);

  // If we have a current detail item, show the detail view
  if (currentDetailItem) {
    return (
      <DetailContainer>
        <DetailHeader ref={detailHeaderRef}>
          <HeaderTitle>
            <h2>Cashiering Detail</h2>
            <p>Review and manage cashiering requests</p>
          </HeaderTitle>
          <BackButton onClick={handleBackToList}>
            <i className="fas fa-arrow-left"></i>
            Back to List
          </BackButton>
        </DetailHeader>
        <DetailContent>
          <Breadcrumb>
            <span>Admin</span>
            <span>/</span>
            <span>Cashiering</span>
            <span>/</span>
            <span className="current">Detail</span>
          </Breadcrumb>
          <AdminCashieringDetail
            id={currentDetailItem.id}
            entityType={currentDetailItem.entityType}
            accountId={currentDetailItem.accountId}
            onViewAllEntries={handleViewAllEntries}
            onSaveComplete={handleSaveComplete}
          />
        </DetailContent>
      </DetailContainer>
    );
  }

  return (
    <>
      {toast &&
        createPortal(
          <ToastContainer>
            <Toast success={toast.success} role="alert" aria-live="assertive">
              <i className="fal fa-check-circle" />
              {toast.message}
              <ToastClose onClick={() => setToast(null)} aria-label="Close notification">
                &times;
              </ToastClose>
            </Toast>
          </ToastContainer>,
          document.body
        )}
      <TableContainer>
        <TableHeader>
          <TableTitle>
            <h2>Cashiering Management</h2>
            <p>Review and manage cashiering requests</p>
          </TableTitle>
        </TableHeader>
        <TableContent>
          {selectedRowIndex !== undefined && (
            <div
              style={{
                padding: '8px 16px',
                marginBottom: '8px',
                background: 'rgba(25, 186, 180, 0.1)',
                boxShadow: '0 0 0 2px rgba(25, 186, 180, 0.3) inset',
                borderRadius: '4px',
                fontSize: '0.75rem',
                color: 'rgba(255, 255, 255, 0.8)',
                boxSizing: 'border-box',
              }}
            >
              <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
              Row highlighted. Press{' '}
              <kbd
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  fontSize: '0.7rem',
                }}
              >
                ESC
              </kbd>{' '}
              to clear.
            </div>
          )}
          <TableLayout>
            <div className="table-section">
              <CashieringTable
                handleSetSelectedRow={handleSetSelectedRowIndex}
                selectedRowIndex={selectedRowIndex}
                CashieringAdminReviewColumns={columnsWithViewDetail}
                tableId={tableId}
                shouldScrollToSelected={shouldScrollToSelected}
                onScrollComplete={() => {
                  setShouldScrollToSelected(false);
                }}
              />
            </div>
            <div className="filters-section">
              <CashieringFilters
                handleSetSelectedRow={handleSetSelectedRowIndex}
                CashieringAdminReviewColumns={columnsWithViewDetail}
                defaultSearchValue={searchValue}
              />
            </div>
          </TableLayout>
        </TableContent>
      </TableContainer>
      {toast && toast.success && setTimeout(() => setToast(null), 3000)}
    </>
  );
};
